/**
 * PII & Anonymization Panel (plugin-only)
 *
 * Run PII check on report content; display matches (emails, proper nouns, blocklist);
 * Approve (no sensitive data) or Edit and re-check. Retroactive allowlist built on approve.
 */

import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Button, Badge, LoadingSpinner, piiScanReport, piiApproveReport, piiDisapproveReport, piiAddToAllowlist, getReport } from '@tracekit/core';
import type { PIIMatch, PIIScanResult } from '@tracekit/core';
import { ShieldCheck, RefreshCw, CheckCircle, AlertTriangle, Mail, User, FileText, XCircle, ChevronDown, ChevronRight, ClipboardCopy, Check } from 'lucide-react';

export interface PIIAnonymizationPanelProps {
  reportId: string;
}

const matchTypeLabel: Record<string, string> = {
  email: 'Email',
  proper_noun: 'Proper noun',
  blocklist: 'Blocklist term',
};

const matchTypeIcon: Record<string, React.ReactNode> = {
  email: <Mail className="w-4 h-4" />,
  proper_noun: <User className="w-4 h-4" />,
  blocklist: <FileText className="w-4 h-4" />,
};

const ROWS_PER_PAGE = 20;

export function PIIAnonymizationPanel({ reportId }: PIIAnonymizationPanelProps) {
  const queryClient = useQueryClient();
  const [scanResult, setScanResult] = useState<PIIScanResult | null>(null);
  const [isScanning, setIsScanning] = useState(false);

  const { data: report } = useQuery({
    queryKey: ['report', reportId],
    queryFn: () => getReport(reportId),
    enabled: !!reportId,
  });

  const piiApproved = !!(report?.pii_reviewed_at ?? report?.meta?.pii_reviewed_at);
  const piiReviewedBy = report?.pii_reviewed_by ?? report?.meta?.pii_reviewed_by;
  const piiReviewedAt = report?.pii_reviewed_at ?? report?.meta?.pii_reviewed_at;

  const scanMutation = useMutation({
    mutationFn: () => piiScanReport(reportId),
    onSuccess: (data) => {
      setScanResult(data);
    },
    onError: (err: unknown) => {
      setScanResult({
        matches: [],
        scanned_at: new Date().toISOString(),
        section_count: 0,
        error: err instanceof Error ? err.message : 'Scan failed',
      });
    },
  });

  const approveMutation = useMutation({
    mutationFn: () => piiApproveReport(reportId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report', reportId] });
    },
    onError: (err: unknown) => {
      console.error('[PII Panel] Approve failed:', err);
    },
  });

  const disapproveMutation = useMutation({
    mutationFn: () => piiDisapproveReport(reportId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report', reportId] });
    },
    onError: (err: unknown) => {
      console.error('[PII Panel] Disapprove failed:', err);
    },
  });

  const matches = scanResult?.matches ?? [];
  const byType = useMemo(() => {
    const g: Record<string, PIIMatch[]> = { email: [], proper_noun: [], blocklist: [] };
    for (const m of matches) {
      const t = m.type in g ? m.type : 'blocklist';
      g[t].push(m);
    }
    return g;
  }, [matches]);

  const [expandedType, setExpandedType] = useState<string | null>(null);
  const [pageByType, setPageByType] = useState<Record<string, number>>({});

  const addToAllowlistMutation = useMutation({
    mutationFn: ({ term }: { term: string }) => piiAddToAllowlist(reportId, term),
    onSuccess: (_, { term }) => {
      setScanResult((prev) =>
        prev && prev.matches
          ? { ...prev, matches: prev.matches.filter((m) => m.value !== term) }
          : prev
      );
    },
    onError: (err: unknown) => {
      console.error('[PII Panel] Add to allowlist failed:', err);
    },
  });

  const [copiedValue, setCopiedValue] = useState<string | null>(null);
  const handleCopyForFind = (value: string) => {
    navigator.clipboard.writeText(value).then(() => {
      setCopiedValue(value);
      setTimeout(() => setCopiedValue(null), 2000);
    });
  };

  const handleRunCheck = async () => {
    setIsScanning(true);
    try {
      await scanMutation.mutateAsync();
    } finally {
      setIsScanning(false);
    }
  };

  const handleApprove = async () => {
    await approveMutation.mutateAsync();
  };

  const handleDisapprove = async () => {
    await disapproveMutation.mutateAsync();
  };

  if (piiApproved) {
    return (
      <Card>
        <div className="p-6">
          <div className="flex items-center gap-3 text-green-600 dark:text-green-400 mb-4">
            <CheckCircle className="h-8 w-8" />
            <div>
              <p className="font-medium">PII review completed</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Reviewed by {piiReviewedBy ?? 'Unknown'} on{' '}
                {piiReviewedAt ? new Date(piiReviewedAt).toLocaleString() : '—'}
              </p>
            </div>
          </div>
          <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
            You can run the check again to re-scan after editing, or clear approval to require a new review.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={handleRunCheck} disabled={isScanning}>
              {isScanning ? <LoadingSpinner className="w-4 h-4" /> : <RefreshCw className="w-4 h-4" />}
              {isScanning ? ' Scanning...' : ' Run PII check again'}
            </Button>
            <Button
              variant="outline"
              onClick={handleDisapprove}
              disabled={disapproveMutation.isPending}
              leftIcon={disapproveMutation.isPending ? <LoadingSpinner className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
              className="text-amber-600 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300 border-amber-300 dark:border-amber-600"
            >
              {disapproveMutation.isPending ? 'Clearing...' : 'Clear approval (require re-review)'}
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  if (!scanResult && !scanMutation.isPending && !scanMutation.isError) {
    return (
      <Card>
        <div className="text-center py-8">
          <ShieldCheck className="h-12 w-12 mx-auto text-gray-400 mb-4" />
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Run a PII check to find emails, proper nouns, and blocklist terms. Approve when no sensitive data remains.
          </p>
          <Button
            onClick={handleRunCheck}
            disabled={isScanning}
            leftIcon={isScanning ? <LoadingSpinner /> : <ShieldCheck className="w-4 h-4" />}
          >
            {isScanning ? 'Scanning...' : 'Run PII check'}
          </Button>
        </div>
      </Card>
    );
  }

  if (isScanning || scanMutation.isPending) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
          <span className="ml-3 text-gray-600 dark:text-gray-400">Scanning content...</span>
        </div>
      </Card>
    );
  }

  if (scanResult?.error) {
    return (
      <Card>
        <div className="text-center py-8">
          <AlertTriangle className="h-12 w-12 mx-auto text-red-400 mb-4" />
          <p className="text-red-600 dark:text-red-400 mb-4">{scanResult.error}</p>
          <Button onClick={handleRunCheck} variant="outline">
            Retry
          </Button>
        </div>
      </Card>
    );
  }

  const hasMatches = matches.length > 0;

  const renderMatchTable = (items: PIIMatch[], typeKey: string, maxVisible: number = ROWS_PER_PAGE) => {
    const page = pageByType[typeKey] ?? 1;
    const start = (page - 1) * maxVisible;
    const visible = items.slice(start, start + maxVisible);
    const hasMore = items.length > start + maxVisible;
    const totalPages = Math.ceil(items.length / maxVisible);

    return (
      <div className="rounded border border-gray-200 dark:border-gray-700 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="text-left py-1.5 px-2 font-medium w-8">#</th>
              <th className="text-left py-1.5 px-2 font-medium">Value</th>
              <th className="text-left py-1.5 px-2 font-medium max-w-[180px]">Context</th>
              <th className="text-right py-1.5 px-2 font-medium w-[200px]">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {visible.map((m, i) => (
              <tr key={`${m.value}-${start + i}`} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                <td className="py-1 px-2 text-gray-500">{start + i + 1}</td>
                <td className="py-1 px-2 font-mono text-xs break-all">{m.value}</td>
                <td className="py-1 px-2 text-gray-600 dark:text-gray-400 truncate max-w-[180px]" title={m.snippet}>
                  {m.snippet}
                </td>
                <td className="py-1 px-2 text-right">
                  <div className="flex items-center justify-end gap-1 flex-wrap">
                    <Button
                      variant="ghost"
                      size="sm"
                      title="Copy value to find in document (Ctrl+F)"
                      onClick={() => handleCopyForFind(m.value)}
                      className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 shrink-0"
                    >
                      {copiedValue === m.value ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : (
                        <ClipboardCopy className="w-4 h-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      title="Add to project allowlist — won't flag in any report in this project"
                      onClick={() => addToAllowlistMutation.mutate({ term: m.value })}
                      disabled={addToAllowlistMutation.isPending}
                      className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 text-xs shrink-0"
                    >
                      Don't flag again (project)
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {hasMore && (
          <div className="flex items-center justify-between px-2 py-1.5 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
            <span className="text-xs text-gray-500">
              Showing {start + 1}&ndash;{start + visible.length} of {items.length}
            </span>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPageByType((p) => ({ ...p, [typeKey]: page - 1 }))}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPageByType((p) => ({ ...p, [typeKey]: page + 1 }))}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <Card>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">PII & Anonymization</h3>
            <Badge variant={hasMatches ? 'warning' : 'success'}>
              {hasMatches
                ? scanResult?.truncated
                  ? `Showing ${matches.length} of ${scanResult?.total_count ?? matches.length} (capped)`
                  : `${matches.length} item(s) found`
                : 'No PII detected'}
            </Badge>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Scanned at {scanResult?.scanned_at ? new Date(scanResult.scanned_at).toLocaleString() : '—'}.{' '}
            {scanResult?.truncated ? (
              <span className="text-amber-600 dark:text-amber-400">Showing first 300 matches (emails, blocklist, then proper nouns). Use &quot;Don&apos;t flag again (project)&quot; to add terms to the project allowlist. </span>
            ) : hasMatches ? (
              'Review and edit the document, use "Don\'t flag again" for false positives, or approve when done.'
            ) : (
              'No emails, blocklist terms, or flagged proper nouns found.'
            )}
          </p>

          {hasMatches && (
            <div className="space-y-3 mb-4">
              {(['email', 'proper_noun', 'blocklist'] as const).map((typeKey) => {
                const items = byType[typeKey];
                if (items.length === 0) return null;
                const isExpanded = expandedType === typeKey || expandedType === null;
                return (
                  <div key={typeKey} className="border rounded-lg border-gray-200 dark:border-gray-700 overflow-hidden">
                    <button
                      type="button"
                      className="w-full flex items-center gap-2 py-2 px-3 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-750 text-left"
                      onClick={() => setExpandedType(isExpanded && expandedType === typeKey ? null : typeKey)}
                    >
                      {isExpanded && expandedType === typeKey ? (
                        <ChevronDown className="w-4 h-4 shrink-0" />
                      ) : (
                        <ChevronRight className="w-4 h-4 shrink-0" />
                      )}
                      <span className="inline-flex items-center gap-1.5">
                        {matchTypeIcon[typeKey] ?? <FileText className="w-4 h-4" />}
                        {matchTypeLabel[typeKey] ?? typeKey}
                      </span>
                      <Badge variant="secondary" className="ml-2">
                        {items.length}
                      </Badge>
                    </button>
                    {(expandedType === typeKey || expandedType === null) && (
                      <div className="p-2">
                        {renderMatchTable(items, typeKey, ROWS_PER_PAGE)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={handleRunCheck} disabled={isScanning}>
              {isScanning ? <LoadingSpinner className="w-4 h-4" /> : <RefreshCw className="w-4 h-4" />}
              Edit and re-check
            </Button>
            <Button
              onClick={handleApprove}
              disabled={approveMutation.isPending}
              leftIcon={approveMutation.isPending ? <LoadingSpinner /> : <CheckCircle className="w-4 h-4" />}
            >
              {approveMutation.isPending ? 'Saving...' : 'Approve (no sensitive data)'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
